import {Observable} from 'rxjs';
import {APIResponse, Filter, SimpleMultiSeries, SimpleUnitType} from '@core/interfaces/system/system-common';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {KpiData} from '@theme/components/charts/chart-kpi/chart-kpi.component';
import {KpiType} from '@core/interfaces/engin/program-management/segment';
import {DownloadURL} from '@core/interfaces/engin/load-forecast/load-forecast';
import {OutcomeMaintainType, PmDataRequest} from '@core/interfaces/engin/program-management/pm-reporting';
import {title} from 'process';

/*
 * Response
 */
export interface Program {
    id: number;
    createdOn: Date;
    updatedOn: Date;
    name: string;
    description: string;
    segmentId: number;
    ownerId: string;
    ownerName: string;
    kpiList: KpiType[];
    scope: Filter[];
}

export interface ProgramSummaryResponseDto {
    totalProjects: number;
    projectsWithAlternatives: number;
    projectsWithoutAlternatives: number;
    projectsWithoutCalculationResults: number;
    projectsWithoutAssets: number;
    projectsWithPartialAssets: number;
}

export interface ProgramDto extends Program {
    budgetMethod: ProgramBudgetType;
    investmentPriority: InvestmentPriorityType;
    segmentName: string;
    projectCount: number;
}

export interface ProgramFullDto extends ProgramDto {
    budgets: ProgramBudget[];
}

export interface ProgramParam {
    id: number;
    programId: number;
    sensitivityId: number;
    budgetMethod: ProgramBudgetType;
    investmentPriority: InvestmentPriorityType;
}

export interface ProgramBudget {
    id: number;
    programId: number;
    sensitivityId: number;
    startYear: number;
    endYear: number;
    budgetUnit: SimpleUnitType;
    budgetValue: number;
    scope: Filter[];
}

export interface ProgramBudgetOutcome {
    id: number;
    startYear: number;
    endYear: number;
    budgetUnit: SimpleUnitType;
    scope: string[];
    budgetValue: number;
}

/*
 * Request
 */
export interface ProgramCreateDto {
    name: string;
    description: string;
    segmentId: number;
    ownerId: string;
    ownerName: string;
    kpiList: KpiType[];
    assetClassList: string[];
    investmentPriority: InvestmentPriorityType;
    budgetMethod: ProgramBudgetType;
    budgetRows: ProgramBudgetCreateDto[];
    sensitivityId: number;
    workflowItemId: number;
}

export interface ProgramBudgetCreateDto {
    sensitivityId: number;
    startYear: number;
    endYear: number;
    budgetUnit: SimpleUnitType;
    budgetValue: number;
    assetClassList: string[];
}

export interface ProgramUpdateDto {
    name: string;
    description: string;
    segmentId: number;
    ownerId: string;
    ownerName: string;
    kpiList: KpiType[];
}

export interface ProgramUpdateBudgetDto {
    programId: number;
    sensitivityId: number;
    budgetMethod: ProgramBudgetType;
    investmentPriority: InvestmentPriorityType;
    budgetsRows: ProgramBudgetUpdateDto[];
}

export interface ProgramBudgetUpdateDto {
    id: number;
    startYear: number;
    endYear: number;
    budgetUnit: SimpleUnitType;
    budgetValue: number;
    assetClassList: string[];
}

export interface ProgramBudgetRequest extends PmDataRequest {
    requestDtos: ProgramBudget[];
}

export interface ProgramBudgetByOutcomeRequest extends PmDataRequest {
    assetClassList: string[];
    investmentPriority: InvestmentPriorityType;
    outcomeMaintainType: OutcomeMaintainType;
    belowPercent: number;
    startYear: number;
}

/*
 * Enums
 */
export enum ProgramBudgetType {
    UNIFORM = 'UNIFORM',
    UNIQUE = 'UNIQUE',
}

export const ProgramBudgetTypeLabel = {
    [ProgramBudgetType.UNIFORM]: 'Uniform',
    [ProgramBudgetType.UNIQUE]: 'Unique',
};

export enum InvestmentPriorityType {
    CALENDAR_AGE_YEARS = 'CALENDAR_AGE_YEARS',
    CALENDAR_AGE_YEARS_PAST_TUL = 'CALENDAR_AGE_YEARS_PAST_TUL',
    CALENDAR_AGE_COUNT_PAST_TUL = 'CALENDAR_AGE_COUNT_PAST_TUL',
    EFFECTIVE_AGE_YEARS = 'EFFECTIVE_AGE_YEARS',
    EFFECTIVE_AGE_YEARS_PAST_TUL = 'EFFECTIVE_AGE_YEARS_PAST_TUL',
    EFFECTIVE_AGE_COUNT_PAST_TUL = 'EFFECTIVE_AGE_COUNT_PAST_TUL',
    HEALTH_TOTAL = 'HEALTH_TOTAL',
    HEALTH_BELOW_WORST = 'HEALTH_BELOW_WORST',
    HEALTH_COUNT_BELOW_WORST = 'HEALTH_COUNT_BELOW_WORST',
    RISK_CURRENT_MONETIZED_TOTAL = 'RISK_CURRENT_MONETIZED_TOTAL',
    RISK_CURRENT_MONETIZED_CUSTOMER = 'RISK_CURRENT_MONETIZED_CUSTOMER',
    RISK_CURRENT_MONETIZED_FINANCIAL = 'RISK_CURRENT_MONETIZED_FINANCIAL',
    RISK_CURRENT_MONETIZED_ENVIRONMENT = 'RISK_CURRENT_MONETIZED_ENVIRONMENT',
    RISK_CURRENT_MONETIZED_COLLATERAL = 'RISK_CURRENT_MONETIZED_COLLATERAL',
    RISK_CURRENT_CI = 'RISK_CURRENT_CI',
    RISK_CURRENT_CHI = 'RISK_CURRENT_CHI',
    ECONOMIC_TCO = 'ECONOMIC_TCO',
}

export const InvestmentPriorityTypeLabel = {
    [InvestmentPriorityType.HEALTH_TOTAL]: 'Health - Total',
    [InvestmentPriorityType.HEALTH_BELOW_WORST]: 'Health - Total below worst category',
    [InvestmentPriorityType.HEALTH_COUNT_BELOW_WORST]: 'Health - Assets in worst category',
    [InvestmentPriorityType.CALENDAR_AGE_YEARS]: 'Calendar Age - Total years',
    [InvestmentPriorityType.CALENDAR_AGE_YEARS_PAST_TUL]: 'Calendar Age - Years past TUL',
    [InvestmentPriorityType.CALENDAR_AGE_COUNT_PAST_TUL]: 'Calendar Age - Assets past TUL',
    [InvestmentPriorityType.EFFECTIVE_AGE_YEARS]: 'Effective Age - Total years',
    [InvestmentPriorityType.EFFECTIVE_AGE_YEARS_PAST_TUL]: 'Effective Age - Years past TUL',
    [InvestmentPriorityType.EFFECTIVE_AGE_COUNT_PAST_TUL]: 'Effective Age - Assets past TUL',
    [InvestmentPriorityType.RISK_CURRENT_MONETIZED_TOTAL]: 'Risk - Total (monetized)',
    [InvestmentPriorityType.RISK_CURRENT_MONETIZED_CUSTOMER]: 'Risk - Customer (monetized)',
    [InvestmentPriorityType.RISK_CURRENT_MONETIZED_FINANCIAL]: 'Risk - Financial (monetized)',
    [InvestmentPriorityType.RISK_CURRENT_MONETIZED_ENVIRONMENT]: 'Risk - Environment (monetized)',
    [InvestmentPriorityType.RISK_CURRENT_MONETIZED_COLLATERAL]: 'Risk - Collateral (monetized)',
    [InvestmentPriorityType.RISK_CURRENT_CI]: 'Risk - CI',
    [InvestmentPriorityType.RISK_CURRENT_CHI]: 'Risk - CHI',
    [InvestmentPriorityType.ECONOMIC_TCO]: 'Economics - TCO',
};

export interface RunPrioritizationRequest {
    programId: number;
    workflowItemId: number;
}

export interface PrioritizationStatusDto {
    programId: number;
    workflowItemId: number;
    status: PrioritizationStatus;
    message: string;
    lastUpdatedOn: Date;
}

export enum PrioritizationStatus {
    NOT_STARTED = 'NOT_STARTED',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    INVALIDATED = 'INVALIDATED',
}

export const PrioritizationStatusLabel = {
    [PrioritizationStatus.NOT_STARTED]: 'Not Started',
    [PrioritizationStatus.IN_PROGRESS]: 'In Progress',
    [PrioritizationStatus.COMPLETED]: 'Completed',
    [PrioritizationStatus.FAILED]: 'Failed',
    [PrioritizationStatus.INVALIDATED]: 'Invalidated',
};

export interface InvestmentPriorityDto {
    key: string;
    title: string;
    description: string;
}

export abstract class ProgramService {
    /*
     * APIs for Programs model
     */
    abstract createProgram(program: ProgramCreateDto): Observable<APIResponse<Program>>;

    abstract editProgramDetails(programId: number, program: ProgramUpdateDto): Observable<APIResponse<Program>>;

    abstract deleteProgram(programId: number): Observable<APIResponse<boolean>>;

    abstract editProgramBudget(
        workflowItemId: number,
        updateDto: ProgramUpdateBudgetDto,
    ): Observable<APIResponse<Program>>;

    abstract getProgramList(params?: Filter[], pageSize?: number): Observable<Program[]>;

    /*
     * APIs for ProgramDto
     */
    abstract getProgramDto(programId: number, request: PmDataRequest): Observable<APIResponse<ProgramDto>>;

    abstract getProgramDtoList(request: PmDataRequest, params: Filter[], pageSize?: number): Observable<ProgramDto[]>;

    abstract getProgramDtoListCsv(request: PmDataRequest, paramsString?: string): Observable<APIResponse<DownloadURL>>;

    abstract getProgramDtoListTable(request: PmDataRequest, paramsString?: string): Observable<DataSource>;

    /*
     * APIs for ProgramFullDto
     */
    abstract getProgramDtoFull(programId: number, req: PmDataRequest): Observable<APIResponse<ProgramFullDto>>;

    /*
     * APIs related to analysis of results and budgets
     */
    abstract findProgramKpis(programId: number, req: PmDataRequest): Observable<APIResponse<KpiData[]>>;

    abstract getBudgetChart(req: ProgramBudgetRequest): Observable<APIResponse<SimpleMultiSeries<number>>>;

    abstract getProgramBudgetByOutcomes(
        req: ProgramBudgetByOutcomeRequest,
    ): Observable<APIResponse<ProgramBudgetOutcome[]>>;

    /*
     * Other support APIs
     */
    abstract getInvestmentPriorityOptions(
        availableOutcomes?: boolean,
    ): Observable<APIResponse<InvestmentPriorityDto[]>>;

    abstract getKpiOptions(): Observable<APIResponse<KpiType[]>>;

    abstract validateProgramName(programName: string): Observable<boolean>;

    /*
     * Helpers - call other APIs
     */
    abstract runPrioritization(request: RunPrioritizationRequest): Observable<PrioritizationStatusDto>;
    abstract runPrioritizationForAllPrograms(workflowItemId: number): Observable<Boolean>;
    abstract getPrioritizationStatus(programId: number, workflowItemId: number): Observable<PrioritizationStatusDto>;

    abstract findAllBySegment(filter: Filter[], request: PmDataRequest): Observable<ProgramDto[]>;
}
